import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  UserCheck,
  Building,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  TrendingUp,
  FileText,
  Star,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';

interface BusinessLayoutProps {
  children: React.ReactNode;
}

const BusinessLayout: React.FC<BusinessLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notifications] = useState(3);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { path: '/employer/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/employer/jobs', icon: Briefcase, label: 'My Jobs' },
    { path: '/employer/applications', icon: Users, label: 'Applications' },
    { path: '/employer/shortlisted', icon: Star, label: 'Shortlisted' },
    { path: '/employer/hired', icon: UserCheck, label: 'Hired' },
    { path: '/employer/profile', icon: Building, label: 'Company Profile' },
    { path: '/employer/settings', icon: Settings, label: 'Settings' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-blue-900 to-blue-800 text-white transition-all duration-300 flex flex-col shadow-xl`}>
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-blue-700/50">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <Briefcase className="w-4 h-4" />
              </div>
              <div>
                <h1 className="text-lg font-bold">BSAF</h1>
                <p className="text-xs text-blue-200">Employer Portal</p>
              </div>
            </div>
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)} 
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Stats Summary */}
        {sidebarOpen && (
          <div className="p-4 border-b border-blue-700/50">
            <div className="bg-white/10 rounded-lg p-3">
              <div className="flex items-center gap-2 text-blue-200 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>Recruitment Status</span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                <div className="bg-white/10 rounded py-1">
                  <p className="text-lg font-bold">{notifications}</p>
                  <p className="text-xs text-blue-200">New</p>
                </div>
                <div className="bg-white/10 rounded py-1">
                  <p className="text-lg font-bold">0</p>
                  <p className="text-xs text-blue-200">Shortlisted</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isActive(item.path) 
                    ? 'bg-white text-blue-900 font-medium shadow-lg' 
                    : 'text-blue-100 hover:bg-white/10'
                }`}
                title={!sidebarOpen ? item.label : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Post Job CTA */}
        {sidebarOpen && (
          <div className="p-4 border-t border-blue-700/50">
            <Link
              to="/employer/post-job"
              className="flex items-center justify-center gap-2 w-full py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors shadow-lg"
            >
              <Briefcase className="w-5 h-5" />
              Post New Job
            </Link>
          </div>
        )}

        {/* User Section */}
        <div className="p-4 border-t border-blue-700/50">
          {sidebarOpen ? (
            <div className="mb-3">
              <p className="text-sm font-medium truncate">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-blue-200 truncate">{user?.email}</p>
            </div>
          ) : null}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-2 w-full text-blue-100 hover:bg-white/10 rounded-lg transition-colors ${
              !sidebarOpen ? 'justify-center' : ''
            }`}
            title={!sidebarOpen ? 'Logout' : undefined}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {menuItems.find(item => isActive(item.path))?.label || 'Dashboard'}
              </h2>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* Notifications */}
              <button className="relative p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                <Bell className="w-5 h-5" />
                {notifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {notifications}
                  </span>
                )}
              </button>

              {/* User Avatar */}
              <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-gray-50">
          {children}
        </main>
      </div>
    </div>
  );
};

export default BusinessLayout;
